import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { IonSelect } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";
import { ClientSettings } from "src/model/class/client-settings";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { NetworkService } from "src/service/network.service";
import {
  clientSettingsUpdateEnableRemobteLogAction,
  clientSettingsUpdateRemoteLogLevelAction,
} from "src/state/actions/client-settings.actions";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { SelectFieldOption } from "../select-field/select-field.component";

@Component({
    selector: "app-options-log",
    templateUrl: "./options-log.component.html",
    styleUrls: ["./options-log.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OptionsLogComponent
  implements OnInit, iTranslation, iConnToolbarColour {

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectClientSettingsSub: Subscription = null;

  //#endregion

  private _translateSubscription: Subscription = null;
  private _clientSettings: ClientSettings = null;
  private _networkServiceSubscription: Subscription = null;

  //#region Translated strings
  public lblundo: string = "";
  public lblSave: string = "";
  //#endregion

  public logLevelString: string = "";
  public formLogSettings: UntypedFormGroup = null;
  public enumLog = LogLevel;
  /** Log levels for the select field (labels are translation keys) */
  public readonly logLevelOptions: Array<SelectFieldOption> = [
    { value: LogLevel.LOG_ALL, label: "dictionary.always" },
    { value: LogLevel.LOG_INFO, label: "dictionary.info" },
    { value: LogLevel.LOG_WARNING, label: "dictionary.warnings" },
    { value: LogLevel.LOG_ERROR, label: "dictionary.errors" },
    { value: LogLevel.LOG_NONE, label: "dictionary.never" },
  ];

  @ViewChild("logLevelSelect", { static: false })
  logLevelSelectRef: IonSelect;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  constructor(
    private _fb: UntypedFormBuilder,
    private _translateService: TranslateService,
    private _networkService: NetworkService,
    private _store: Store
  ) { }

  ngOnInit() {
    this.initializeForm();
    this.loadTranslation();
    this.subscribeTranslationEvent();

    this.subscribeValueFormChange();
    this.subscribeNetworkServiceData();
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();

    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }

    this._networkServiceSubscription.unsubscribe();
  }

  /**
   * Handle click of log level select option
   */
  public onClickLogLevelOption(): void {
    this.logLevelSelectRef.open();
  }

  //#region Translation Service
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.undo", "dictionary.save"])
      .subscribe((translation) => {
        this.lblundo = translation["dictionary.undo"];
        this.lblSave = translation["dictionary.save"];

        //DON'T MMOVE IT: It requried loaded translation to perform input log level filled
        this.subscribeToClientSettings();
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Initialize form log settings
   */
  private initializeForm(): void {
    this.formLogSettings = this._fb.group({
      logEnable: new UntypedFormControl(false),
      logLevel: new UntypedFormControl(LogLevel.LOG_NONE),
    });
  }

  /**
   * Update form log with specific client settings value
   */
  private updateFormFields(): void {
    this.formLogSettings.patchValue(
      {
        logEnable: this._clientSettings.remoteLogEnable,
        logLevel: this._clientSettings.remoteLogLevel,
      },
      {
        emitEvent: false,
      }
    );
  }

  /**
   * Subscribe to value change event
   */
  private subscribeValueFormChange(): void {
    this.formLogSettings
      .get("logEnable")
      .valueChanges.subscribe((newLogEnableValue: boolean) => {
        this._store.dispatch(
          clientSettingsUpdateEnableRemobteLogAction({
            enableRemoteLog: newLogEnableValue,
          })
        );
      });

    this.formLogSettings
      .get("logLevel")
      .valueChanges.subscribe((newLogLevelValue: number) => {
        this._store.dispatch(
          clientSettingsUpdateRemoteLogLevelAction({
            remoteLogLevel: newLogLevelValue,
          })
        );

        this.fillLoadLevelString(newLogLevelValue);
      });
  }

  /**
   * Fill remote log level as string
   *
   * @param logLevel Log level number value
   */
  private fillLoadLevelString(logLevel: LogLevel): void {
    this.logLevelString = EnumStringConverter.logLevelAsStringEnum(
      logLevel,
      this._translateService
    );
  }

  /**
   * Subscribe to client settign service
   */
  private subscribeToClientSettings(): void {
    this._selectClientSettingsSub = this._store
      .select(clientSettings)
      .pipe(
        filter((clientSettings) => clientSettings !== null),
        tap((clientSettings) => (this._clientSettings = clientSettings)),
        tap((clientSettings) =>
          this.fillLoadLevelString(this._clientSettings.remoteLogLevel)
        ),
        take(1)
      )
      .subscribe((clientSettings: ClientSettings) => {
        this.updateFormFields();
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   * 
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectClientSettingsSub) {
      this._selectClientSettingsSub.unsubscribe();
    }

  }

  //#endregion


  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }
}
